import { useTranslation } from 'react-i18next';
import React, { memo, useMemo, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Virtuoso } from 'react-virtuoso';
import cx from 'clsx';

import { Modal, UnionAvatar, FollowButton, HelperTooltip, UserCardPopup } from '@/components';
import { getOfficialPath, isOfficalAddress, openNewWindow, useCurrentUserId } from '@/utils';
import { IconRcFeedRank } from '@/assets/stream';

import { ArticleDepositor } from '../../types';
import { changeArticleDepositorsFollowing, useArticleDepositors } from '../../hooks';
import { transAuthor } from '../../utils';
import style from './index.module.scss';

const SkeletonList = memo(({ count = 5 }) => (
  <>
    {Array(count).fill(
      <div className={style.item}>
        <div className={style.leftContent}>
          <Skeleton circle height={48} width={48} className={style.avatar} />
          <div className={style.operationUserMain}>
            <Skeleton width={80} height={18} className={style.commentHead} />
            <Skeleton width={40} height={18} className={style.commentHead} />
          </div>
        </div>
        <Skeleton className={style.followBtn} />
      </div>,
    )}
  </>
));

const { CommonModal } = Modal;

export const DepositorsModal = ({
  on,
  turnOff,
  articleId,
}: {
  articleId: number;
  on?: boolean;
  turnOff?: () => void;
}) => {
  const { t } = useTranslation();

  const address = useCurrentUserId();
  const {
    result: users,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useArticleDepositors(articleId);

  const turnOffModal = useCallback(() => {
    turnOff && turnOff();
  }, [turnOff]);

  const fetchMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleFollowChange = useCallback(
    userId => {
      changeArticleDepositorsFollowing(articleId, userId, address);
    },
    [articleId, address],
  );

  return (
    <CommonModal on={on} containerClassName={style.userListModal} turnOff={turnOffModal}>
      <div className={style.userList}>
        <div className={style.header}>
          {t('DepositorsModal.13p6rw2')}
          <span className={style.headerTip}>({users?.total_count})</span>
        </div>
        {isLoading ? (
          <div className={style.list}>
            <SkeletonList />
          </div>
        ) : users?.depositors?.length ? (
          <div className={style.list}>
            <div className={style.listHeader}>
              <div className={style.leftContent}>{t('DepositorsModal.1k157')}</div>
              <div className={style.deposit}>{t('DepositorsModal.11ky7i6')}</div>
              <div />
            </div>
            <Virtuoso
              style={{ height: '500px', marginRight: -24 }}
              data={users?.depositors}
              overscan={20}
              endReached={fetchMore}
              itemContent={(index, item) => (
                <UserItem
                  user={item}
                  key={`${item.id}_${index}`}
                  onFollowChange={handleFollowChange}
                />
              )}
            />
          </div>
        ) : null}
      </div>
    </CommonModal>
  );
};

const UserItem = ({
  className,
  user,
  onFollowChange,
}: {
  className?: string;
  user: ArticleDepositor;
  onFollowChange: (userId: string) => void;
}) => {
  const { t } = useTranslation();

  const address = useCurrentUserId();
  const userId = user.id;
  const isOfficial = isOfficalAddress(userId);

  const handleUserClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      openNewWindow(
        isOfficial
          ? `${getOfficialPath(user as OfficialAccount)}/stream`
          : `/profile/${userId}/stream`,
        '_blank',
      );
    },
    [isOfficial, user, userId],
  );

  const name = useMemo(() => transAuthor({ user }), [user]);

  const handleUserFollowChange = useCallback(() => {
    onFollowChange(userId);
  }, [onFollowChange, userId]);

  return (
    <div className={cx(style.item, className)} key={userId}>
      <div className={style.leftContent}>
        <div onClick={handleUserClick}>
          <UnionAvatar
            className={style.avatar}
            size={48}
            data={user}
            userCardPopup={{
              followCallback: handleUserFollowChange,
            }}
          />
        </div>
        <div className={style.operationUserMain}>
          <div className={style.commentHead}>
            <UserCardPopup
              address={isOfficalAddress(user.id) ? '' : user.id || ''}
              followCallback={handleUserFollowChange}>
              <div onClick={handleUserClick} className={style.uid}>
                {name}
              </div>
            </UserCardPopup>
          </div>
          <HelperTooltip title={t('DepositorsModal.1h0gg1w')}>
            <div className={style.content}>
              <IconRcFeedRank /> <span>{(user as any).rank_at}</span>
            </div>
          </HelperTooltip>
        </div>
      </div>
      <div className={style.deposit}>{user._rewardUsdValue}</div>
      <div>
        {address === userId || isOfficial ? null : (
          <FollowButton address={user.id} userInfo={user as any} className={style.followBtn} />
        )}
      </div>
    </div>
  );
};
